import React, { useEffect, useState } from "react";
import TimeSlotsSection from "./TimeSlotsSection";
import { DAY_ORDER } from "../../../../../lib/barTimeSlots";

export const DEFAULT_SHOW_SLOT_TEMPLATE = () => [
  { id: 1, name: "Early", start_time: "12:00", end_time: "16:00" },
  { id: 2, name: "Mid", start_time: "16:30", end_time: "20:30" },
  { id: 3, name: "Late", start_time: "21:00", end_time: "01:00" },
];

const DAY_LABEL = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

function modeForDay(byDay, d) {
  if (!byDay || !Object.prototype.hasOwnProperty.call(byDay, d)) {
    return "inherit";
  }
  const v = byDay[d];
  if (v === null || v === undefined) return "inherit";
  if (Array.isArray(v) && v.length === 0) return "none";
  if (Array.isArray(v)) return "custom";
  return "inherit";
}

/**
 * @param {object} props
 * @param {{ version?: number, enabled: boolean, defaultSlots: array, byDay?: object }} props.value
 * @param {(v: object) => void} props.onChange — full v2 payload
 * @param {boolean} props.disabled
 * @param {(errors: object) => void} [props.onValidationChange]
 */
const BarTimeSlotsConfigSection = ({
  value,
  onChange,
  disabled = false,
  onValidationChange,
}) => {
  const enabled = value?.enabled !== false;
  const defaultSlots = value?.defaultSlots?.length
    ? value.defaultSlots
    : DEFAULT_SHOW_SLOT_TEMPLATE();
  const byDay = value?.byDay && typeof value.byDay === "object" ? value.byDay : {};

  const update = (patch) => {
    onChange({
      version: 2,
      enabled: patch.enabled !== undefined ? patch.enabled : enabled,
      defaultSlots: patch.defaultSlots ?? defaultSlots,
      byDay: patch.byDay !== undefined ? patch.byDay : byDay,
    });
  };

  const setMode = (day, mode) => {
    const next = { ...byDay };
    if (mode === "inherit") {
      delete next[day];
    } else if (mode === "none") {
      next[day] = [];
    } else if (mode === "custom") {
      next[day] =
        Array.isArray(next[day]) && next[day].length > 0
          ? next[day].map((s) => ({ ...s }))
          : [
              {
                id: Date.now(),
                name: "Slot 1",
                start_time: "12:00",
                end_time: "16:00",
              },
            ];
    }
    update({ byDay: next });
  };

  const setCustomDaySlots = (day, slots) => {
    const next = { ...byDay, [day]: slots };
    update({ byDay: next });
  };

  const applyDefaultToAll = () => {
    update({ byDay: {} });
  };

  const [validationBySection, setValidationBySection] = useState({});

  const setSectionValidation = (sectionKey, errObj) => {
    setValidationBySection((prev) => ({ ...prev, [sectionKey]: errObj || {} }));
  };

  useEffect(() => {
    if (!onValidationChange) return;
    const flat = {};
    Object.entries(validationBySection).forEach(([section, errObj]) => {
      Object.entries(errObj || {}).forEach(([slotId, msg]) => {
        if (msg) flat[`${section}_${slotId}`] = msg;
      });
    });
    onValidationChange(flat);
  }, [validationBySection, onValidationChange]);

  return (
    <div className="bar-time-slots-config">
      <label className="input-group" style={{ marginBottom: "1rem" }}>
        <input
          type="checkbox"
          checked={enabled}
          disabled={disabled}
          onChange={(e) => update({ enabled: e.target.checked })}
        />{" "}
        <span style={{ marginLeft: "0.5rem" }}>
          Use named show times, turn off to schedule with custom start/end times
          only.
        </span>
      </label>

      {enabled && (
        <>
          <p className="input-hint" style={{ marginTop: 0 }}>
            Default time slots apply to every day unless you override a weekday
            below.
          </p>
          <TimeSlotsSection
            slots={defaultSlots}
            onChange={(slots) => update({ defaultSlots: slots })}
            disabled={disabled}
            minSlots={0}
            maxSlots={6}
            onValidationChange={(err) => setSectionValidation("default", err)}
          />
          <div style={{ marginTop: "1rem" }}>
            <button
              type="button"
              className="add-slot-btn"
              onClick={applyDefaultToAll}
              disabled={disabled}
            >
              Reset weekdays to use default
            </button>
          </div>

          <h3
            className="section-title"
            style={{ fontSize: "1rem", marginTop: "1.5rem" }}
          >
            Per-day overrides
          </h3>
          {DAY_ORDER.map((d) => {
            const mode = modeForDay(byDay, d);
            const customSlots = Array.isArray(byDay[d]) ? byDay[d] : [];
            return (
              <div
                key={d}
                style={{
                  marginBottom: "1rem",
                  padding: "0.75rem",
                  border: "1px solid var(--border-color, #ddd)",
                  borderRadius: "8px",
                }}
              >
                <div className="input-group" style={{ marginBottom: "0.5rem" }}>
                  <label className="input-label">{DAY_LABEL[d]}</label>
                  <select
                    className="settings-input"
                    value={mode}
                    disabled={disabled}
                    onChange={(e) => setMode(d, e.target.value)}
                  >
                    <option value="inherit">Same as default</option>
                    <option value="none">No named slots (custom times only)</option>
                    <option value="custom">Custom time slots…</option>
                  </select>
                </div>
                {mode === "custom" && (
                  <TimeSlotsSection
                    slots={customSlots}
                    onChange={(slots) => setCustomDaySlots(d, slots)}
                    disabled={disabled}
                    minSlots={1}
                    maxSlots={6}
                    onValidationChange={(err) =>
                      setSectionValidation(`day_${d}`, err)
                    }
                  />
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

export default BarTimeSlotsConfigSection;
